import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import EmptyState from "@/components/EmptyState";
import { CallButton } from "@/components/outreach/Integrations";
import { ContactProposalButton } from "@/components/outreach/Proposal";
import { ConsentBadge, ContactCallText } from "@/components/outreach/ContactComms";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Upload, Sparkles, Flame, Snowflake, Thermometer, Search, Users, Pencil, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const scoreBadge = {
  hot: { label: "Hot", icon: Flame, cls: "bg-orange-500/12 text-orange-700 dark:text-orange-400 border-orange-500/25" },
  warm: { label: "Warm", icon: Thermometer, cls: "bg-amber-500/12 text-amber-700 dark:text-amber-400 border-amber-500/25" },
  cold: { label: "Cold", icon: Snowflake, cls: "bg-sky-500/12 text-sky-700 dark:text-sky-400 border-sky-500/25" },
};

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [scoring, setScoring] = useState(false);
  const [finding, setFinding] = useState(false);

  const load = async () => {
    const { data } = await api.get("/contacts", { params: { q: q || undefined } });
    setContacts(data);
    const wanted = new URLSearchParams(window.location.search).get("open");
    if (wanted) {
      window.history.replaceState({}, "", "/contacts");
      const c = data.find((x) => x.id === wanted);
      if (c) openContact(c);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const openContact = async (c) => {
    setActive(c); setOpen(true);
    const { data } = await api.get("/activities", { params: { entity_type: "contact", entity_id: c.id } });
    setTimeline(data);
  };

  const scoreLead = async () => {
    if (!active) return;
    setScoring(true);
    try {
      const { data } = await api.post(`/contacts/${active.id}/score`);
      setActive(data);
      await load();
      const { data: t } = await api.get("/activities", { params: { entity_type: "contact", entity_id: active.id } });
      setTimeline(t);
      toast.success(`Scored: ${data.lead_score} (${data.lead_priority})`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setScoring(false); }
  };

  const findEmail = async () => {
    if (!active) return;
    setFinding(true);
    try {
      const { data } = await api.post("/discovery/find-email", { contact_id: active.id });
      setActive({ ...active, email: data.email });
      await load();
      toast.success(`Found ${data.email} (${data.score}% confidence)`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setFinding(false); }
  };

  return (
    <Shell title="Contacts" actions={
      <>
        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" data-testid="btn-import-contacts"><Upload className="w-4 h-4 mr-2" />Import CSV</Button>
          </DialogTrigger>
          <ImportDialog onDone={() => { setImportOpen(false); load(); }} />
        </Dialog>
        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="btn-new-contact"><Plus className="w-4 h-4 mr-2" />New contact</Button>
          </DialogTrigger>
          <NewContactDialog onDone={() => { setNewOpen(false); load(); }} />
        </Dialog>
      </>
    }>
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input data-testid="input-search-contacts" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts..." className="pl-8" />
          </div>
          <div className="text-xs text-muted-foreground mono">{contacts.length} contacts</div>
        </div>
        <div className="border border-border rounded-md overflow-hidden">
          <table className="cf-table w-full text-sm">
            <thead>
              <tr>
                <th className="text-left py-2 px-3 font-medium">Name</th>
                <th className="text-left py-2 px-3 font-medium">Email</th>
                <th className="text-left py-2 px-3 font-medium">Phone</th>
                <th className="text-left py-2 px-3 font-medium">Company</th>
                <th className="text-left py-2 px-3 font-medium">Title</th>
                <th className="text-left py-2 px-3 font-medium">Score</th>
                <th className="text-left py-2 px-3 font-medium">Tags</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => {
                const b = scoreBadge[c.lead_score] || scoreBadge.cold;
                const Icon = b.icon;
                return (
                  <tr key={c.id} className="cursor-pointer" onClick={() => openContact(c)} data-testid={`contact-row-${c.id}`}>
                    <td className="py-2 px-3 font-medium">{c.first_name} {c.last_name}{c.unsubscribed && <span className="ml-2 text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded border bg-rose-500/15 text-rose-500 border-rose-500/30" data-testid={`contact-unsub-${c.id}`}>unsubscribed</span>}</td>
                    <td className="py-2 px-3 mono text-xs text-muted-foreground">{c.email}</td>
                    <td className="py-2 px-3 mono text-xs text-muted-foreground" data-testid={`contact-phone-${c.id}`}><div className="flex items-center gap-1.5">{c.phone || <span className="opacity-40">—</span>}<ConsentBadge contact={c} /><ContactCallText contact={c} compact /></div></td>
                    <td className="py-2 px-3">{c.company_name}</td>
                    <td className="py-2 px-3 text-muted-foreground">{c.title}</td>
                    <td className="py-2 px-3"><span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded border ${b.cls}`}><Icon className="w-3 h-3" />{b.label}</span></td>
                    <td className="py-2 px-3">
                      <div className="flex gap-1 flex-wrap">
                        {(c.tags || []).map((t) => <Badge key={t} variant="outline" className="text-[10px] font-normal">{t}</Badge>)}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {contacts.length === 0 && (
                <tr><td colSpan={7} className="p-4"><EmptyState icon={Users} title="No contacts yet" hint="Import a CSV, add your first contact, or capture buyers through your website chat, forms and rebate calculator." /></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          {active && (
            <>
              <SheetHeader>
                <SheetTitle className="text-xl tracking-tight flex items-center gap-2">{active.first_name} {active.last_name}{active.unsubscribed && <span className="text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded border bg-rose-500/15 text-rose-500 border-rose-500/30" data-testid="sheet-unsubscribed">unsubscribed</span>}</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Field label="Email" value={active.email} mono />
                  <PhoneField contact={active} onSaved={(c) => { setActive(c); setContacts((list) => list.map((x) => (x.id === c.id ? { ...x, phone: c.phone } : x))); }} />
                  <div className="col-span-2 flex items-center gap-2 flex-wrap" data-testid="contact-comms"><ConsentBadge contact={active} editable onChanged={(c) => { setActive(c); setContacts((list) => list.map((x) => (x.id === c.id ? c : x))); }} /><ContactCallText contact={active} />{active.phone && !active.sms_consent && <span className="text-[11px] text-muted-foreground">Texting is off until the buyer opts in (chat, form, calculator) — or click the badge to record verbal consent.</span>}</div>
                  <Field label="Company" value={active.company_name || "—"} />
                  <Field label="Title" value={active.title || "—"} />
                  <Field label="Source" value={active.source || "—"} />
                  <Field label="Timezone" value={active.timezone || "— (uses sequence window)"} mono />
                  <Field label="Score" value={`${active.lead_score || "cold"}${active.lead_priority ? ` · ${active.lead_priority}` : ""}`} />
                  <Field label="Last scored" value={active.lead_scored_at ? `${new Date(active.lead_scored_at).toLocaleString()}${active.lead_score_trigger && active.lead_score_trigger !== "manual" ? ` · auto (${active.lead_score_trigger})` : ""}` : "never"} />
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button data-testid="btn-score-lead" onClick={scoreLead} disabled={scoring} variant="outline" size="sm" className="flex-1">
                    <Sparkles className="w-4 h-4 mr-2" />{scoring ? "Scoring with Jev..." : "Score lead with AI"}
                  </Button>
                  <ContactProposalButton contact={active} />
                  {!active.email && <Button data-testid="btn-find-email" onClick={findEmail} disabled={finding} variant="outline" size="sm" className="flex-1"><Search className="w-4 h-4 mr-2" />{finding ? "Searching Hunter..." : "Find email (Hunter)"}</Button>}
                  <CallButton contact={active} />
                </div>
                <ContactChats contactId={active.id} />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.1em] font-medium text-muted-foreground mb-2">Activity</div>
                  <div className="space-y-2">
                    {timeline.length === 0 && <div className="text-xs text-muted-foreground">No activity yet.</div>}
                    {timeline.map((a) => (
                      <div key={a.id} className="border-l-2 border-border pl-3 py-1">
                        <div className="text-xs mono text-muted-foreground">{a.kind}</div>
                        <div className="text-sm">{a.body}</div>
                        <div className="text-[10px] text-muted-foreground mono">{new Date(a.created_at).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </Shell>
  );
}

function ContactChats({ contactId }) {
  const [chats, setChats] = useState([]);
  const [openId, setOpenId] = useState(null);
  useEffect(() => { setChats([]); setOpenId(null); api.get("/widget/sessions", { params: { contact_id: contactId } }).then((r) => setChats(r.data)).catch(() => {}); }, [contactId]);
  if (!chats.length) return null;
  return (
    <div data-testid="contact-chats">
      <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground mb-2 flex items-center gap-1.5"><MessageSquare className="w-3 h-3" />Website chats ({chats.length})</div>
      <div className="space-y-2">
        {chats.map((c) => (
          <div key={c.id} className="border border-border rounded-md bg-card" data-testid={`contact-chat-${c.id}`}>
            <button type="button" onClick={() => setOpenId(openId === c.id ? null : c.id)} className="w-full text-left px-3 py-2 text-xs flex items-center justify-between gap-2 hover:bg-muted/40 transition-colors" data-testid={`contact-chat-toggle-${c.id}`}>
              <span className="truncate">{c.messages?.[0]?.content || "Chat"}</span>
              <span className="mono text-[10px] text-muted-foreground shrink-0">{c.messages?.length || 0} msgs · {new Date(c.updated_at || c.created_at).toLocaleDateString()}{c.lead_magnet_unlocked_at ? " · guide" : ""}</span>
            </button>
            {openId === c.id && (
              <div className="border-t border-border p-3 space-y-1.5 max-h-72 overflow-y-auto" data-testid={`contact-chat-transcript-${c.id}`}>
                {(c.messages || []).map((m, i) => <div key={i} className={`text-xs rounded px-2 py-1.5 whitespace-pre-wrap ${m.role === "user" ? "bg-primary/10 border border-primary/30 ml-6" : "bg-muted/40 border border-border mr-6"}`}><span className="mono text-[9px] text-muted-foreground">{m.role === "user" ? "Visitor" : "AI"} · {new Date(m.at).toLocaleTimeString()}</span><div>{m.content}</div></div>)}
                <Link to="/inbox" className="text-[11px] text-primary hover:underline" data-testid={`contact-chat-inbox-${c.id}`}>Open in Inbox →</Link>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PhoneField({ contact, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(contact.phone || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(contact.phone || ""); setEditing(false); }, [contact.id, contact.phone]);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.patch(`/contacts/${contact.id}`, { phone: val }); toast.success(data.phone ? `Phone saved: ${data.phone}` : "Phone cleared"); onSaved(data); setEditing(false); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">Phone</div>
      {editing ? (
        <div className="flex items-center gap-1 mt-0.5">
          <Input data-testid="contact-phone-input" value={val} onChange={(e) => setVal(e.target.value)} placeholder="+1 555 123 4567" className="h-7 text-xs mono" autoFocus onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }} />
          <Button size="sm" className="h-7 text-xs" onClick={save} disabled={busy} data-testid="contact-phone-save">Save</Button>
        </div>
      ) : (
        <button type="button" onClick={() => setEditing(true)} className="group text-sm mono text-left flex items-center gap-1.5 hover:text-primary transition-colors" data-testid="contact-phone-edit" title="Edit phone">
          {contact.phone || <span className="text-muted-foreground not-italic">Add phone…</span>}<Pencil className="w-3 h-3 opacity-0 group-hover:opacity-70 transition-opacity" />
        </button>
      )}
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.1em] font-medium text-muted-foreground">{label}</div>
      <div className={`text-sm ${mono ? "mono" : ""}`}>{value}</div>
    </div>
  );
}

function NewContactDialog({ onDone }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", company_name: "", title: "", timezone: "" });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/contacts", form);
      toast.success("Contact created");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };
  return (
    <DialogContent>
      <DialogHeader><DialogTitle>New contact</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2"><Label>First name</Label><Input data-testid="new-contact-first" value={form.first_name} onChange={upd("first_name")} /></div>
          <div className="space-y-2"><Label>Last name</Label><Input data-testid="new-contact-last" value={form.last_name} onChange={upd("last_name")} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2"><Label>Email</Label><Input data-testid="new-contact-email" type="email" value={form.email} onChange={upd("email")} required /></div>
          <div className="space-y-2"><Label>Phone <span className="text-muted-foreground font-normal">(for SMS & calls)</span></Label><Input data-testid="new-contact-phone" value={form.phone} onChange={upd("phone")} placeholder="+1 555 123 4567" className="mono" /></div>
        </div>
        <div className="space-y-2"><Label>Company</Label><Input data-testid="new-contact-company" value={form.company_name} onChange={upd("company_name")} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2"><Label>Title</Label><Input data-testid="new-contact-title" value={form.title} onChange={upd("title")} /></div>
          <div className="space-y-2"><Label>Timezone <span className="text-muted-foreground font-normal">(optional)</span></Label><Input data-testid="new-contact-timezone" value={form.timezone} onChange={upd("timezone")} placeholder="e.g. America/New_York" list="tz-list" /></div>
        </div>
        <datalist id="tz-list">{["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Europe/London", "Europe/Paris", "Europe/Berlin", "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney"].map((t) => <option key={t} value={t} />)}</datalist>
        <Button data-testid="new-contact-submit" type="submit" disabled={loading} className="w-full">{loading ? "Creating..." : "Create contact"}</Button>
      </form>
    </DialogContent>
  );
}

function ImportDialog({ onDone }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/contacts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} contacts (${data.duplicates} duplicates)`);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };
  return (
    <DialogContent>
      <DialogHeader><DialogTitle>Import contacts (CSV)</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div className="text-xs text-muted-foreground">Required column: <span className="mono">email</span>. Optional: <span className="mono">first_name, last_name, company, title, phone, tags, timezone</span></div>
        <Input data-testid="input-csv-file" type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <Button data-testid="btn-csv-import-submit" type="submit" disabled={!file || loading} className="w-full">{loading ? "Importing..." : "Import"}</Button>
      </form>
    </DialogContent>
  );
}
